// Libraries
import { useRef, ChangeEvent, forwardRef } from "react";

// Components

// Types
import { TextAreaProps } from "./TextArea.types";
import classNames from "classnames";

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      id = "",
      name,
      value,
      onChange,
      onBlur,
      disabled = false,
      maxLength,
      required = false,
      rows,
      cols,
      placeholder = "Enter your text here...",
      alert,
      manualResize = false,
      isReadOnly = false,
      className = "",
      label,
    },
    ref
  ) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const charCount = typeof value === "string" ? value.length : 0;

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      if (isReadOnly) return;

      if (textAreaRef.current && !manualResize) {
        textAreaRef.current.style.height = "auto";
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight + 2}px`;
      }
      onChange(event);
    };

    const charsRemaining = maxLength ? maxLength - charCount : 0;

    return (
      <div
        className={classNames("w-full flex flex-col items-start gap-1", {
          "opacity-50": disabled,
        })}
      >
        {/* Render label if provided */}
        {label && (
          <label
            htmlFor={id || name}
            className="text-left text-muted-foreground text-sm min-w-[90px]"
          >
            {label}
            {required && <span className="text-primary ml-1">*</span>}
          </label>
        )}
        <textarea
          id={id}
          name={name}
          ref={ref}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          rows={rows}
          cols={cols}
          className={`w-full bg-muted text-foreground border border-border rounded-md px-3 py-2 focus:outline-none focus:border-primary text-base md:text-sm max-h-[250px] transition-colors duration-200 ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          } ${className}`}
          style={manualResize ? {} : { resize: "none" }}
          maxLength={maxLength}
          readOnly={isReadOnly}
          disabled={disabled}
          required={required}
          data-testid={"textArea"}
        />
        <div className="flex flex-row justify-between items-center w-full">
          {alert && <span className="text-xs text-danger mt-1">{alert.message}</span>}
          {maxLength && (
            <div className="ml-auto text-xs mt-1">
              <span
                className={
                  charsRemaining <= 0
                    ? "text-danger"
                    : charsRemaining <= 50
                      ? "text-primary"
                      : "text-muted-foreground"
                }
              >
                {charCount}
              </span>
              <span className="text-muted-foreground">/{maxLength}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

TextArea.displayName = "TextArea";
